// Cliente HTTP da API Go (chi). Rotas montadas sob /api/feed (ver backend/internal/api/router.go).
// Dev: Vite proxia /api -> :8081. Prod: nginx proxia /api -> erp-backend:8081 (ou VITE_API_BASE).
const API_BASE = import.meta.env.VITE_API_BASE || '/api/feed';

// Spec 008 — a sessão vive em cookie HttpOnly emitido pelo backend; o JS não
// tem (nem deve ter) acesso ao token. Sobra apenas a flag de "sessão ativa"
// para o AuthProvider decidir se tenta reidratar, já que o cookie em si é
// invisível daqui.
const SESSION_FLAG = 'feed_session';

export const hasSession = () =>
  sessionStorage.getItem(SESSION_FLAG) === '1' || localStorage.getItem(SESSION_FLAG) === '1';

// A flag acompanha a durabilidade do cookie emitido pelo backend: com
// "manter conectado" o cookie persiste (localStorage), sem ele o cookie morre
// ao fechar o browser (sessionStorage) — assim o app não tenta reidratar uma
// sessão que já expirou.
export const markSession = (persistente = false) => {
  if (persistente) {
    localStorage.setItem(SESSION_FLAG, '1');
    sessionStorage.removeItem(SESSION_FLAG);
  } else {
    sessionStorage.setItem(SESSION_FLAG, '1');
    localStorage.removeItem(SESSION_FLAG);
  }
};

export const clearSession = () => {
  sessionStorage.removeItem(SESSION_FLAG);
  localStorage.removeItem(SESSION_FLAG);
};

// csrf_token é o único cookie de sessão legível por JS (double-submit):
// o backend compara este valor com o header X-CSRF-Token.
function getCsrfToken() {
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

const MUTATING_METHODS = new Set(['POST', 'PUT', 'DELETE', 'PATCH']);

async function request(path, { method = 'GET', body, auth = true, headers = {} } = {}) {
  const finalHeaders = { ...headers };
  if (!('Content-Type' in finalHeaders)) {
    finalHeaders['Content-Type'] = 'application/json';
  } else if (finalHeaders['Content-Type'] === undefined) {
    delete finalHeaders['Content-Type'];
  }

  if (auth && MUTATING_METHODS.has(method.toUpperCase())) {
    const csrf = getCsrfToken();
    if (csrf) finalHeaders['X-CSRF-Token'] = csrf;
  }

  let finalBody = body;
  if (body && !(body instanceof FormData)) {
    finalBody = JSON.stringify(body);
  }

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: finalHeaders,
    body: finalBody,
    // Envia os cookies de sessão (token HttpOnly + csrf_token).
    credentials: 'include',
  });

  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  if (!res.ok) {
    let friendlyMessage = 'Ocorreu um erro inesperado ao processar sua solicitação.';
    
    // Tratativa amigável baseada no status HTTP e códigos de erro conhecidos
    if (res.status === 401) {
      if (auth) {
        clearSession();
        friendlyMessage = 'Sua sessão expirou. Por favor, faça login novamente.';
      } else {
        friendlyMessage = data?.message || 'Usuário ou senha incorretos.';
      }
    } else if (res.status === 403) {
      friendlyMessage = 'Você não tem permissão para acessar este recurso ou realizar esta ação.';
    } else if (res.status === 404) {
      friendlyMessage = 'O registro ou recurso solicitado não foi encontrado.';
    } else if (res.status === 400 || res.status === 422) {
      friendlyMessage = data?.message || 'Os dados informados são inválidos. Verifique-os e tente novamente.';
    } else if (res.status === 502 || res.status === 504) {
      friendlyMessage = 'O servidor demorou muito para responder ou está indisponível. Tente novamente em instantes.';
    } else if (res.status >= 500) {
      friendlyMessage = 'Ocorreu um erro interno no servidor. Nossa equipe já foi notificada.';
    } else if (data && data.message) {
      friendlyMessage = data.message;
    }

    throw new ApiError(friendlyMessage, res.status, data);
  }

  return data;
}

export class ApiError extends Error {
  constructor(message, status, payload) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

// Anexa o período aos params. Aceita objeto { inicio, fim } (YYYY-MM-DD, novo padrão
// de range por dia) ou string 'YYYY-MM' (legado). O backend aceita ambos.
function appendPeriodo(params, periodo) {
  if (!periodo) return;
  if (typeof periodo === 'object') {
    if (periodo.inicio) params.append('inicio', periodo.inicio);
    if (periodo.fim) params.append('fim', periodo.fim);
  } else {
    params.append('periodo', periodo);
  }
}

export const api = {
  // Auth — o backend Go espera { usuario_login, senha } (ver auth_handler.go)
  login: (usuarioLogin, senha, manterConectado = false) =>
    request('/auth/login', { method: 'POST', body: { usuario_login: usuarioLogin, senha, manter_conectado: manterConectado }, auth: false }),
  googleAuthUrl: (redirectUri) =>
    request(`/auth/google/url?redirect_uri=${encodeURIComponent(redirectUri)}`, { auth: false }),
  googleSSOLogin: (code, redirectUri, manterConectado = false) =>
    request('/auth/google/login', { method: 'POST', body: { code, redirect_uri: redirectUri, manter_conectado: manterConectado }, auth: false }),
  me: () => request('/auth/me'),
  updateMe: (body) => request('/auth/me', { method: 'PUT', body }),
  changePassword: (body) => request('/auth/change-password', { method: 'POST', body }),
  logout: () => request('/auth/logout', { method: 'POST' }),
  // Renova a sessão usando o refresh token (cookie HttpOnly). Precisa de
  // `auth: true` para enviar o X-CSRF-Token: o refresh é uma mutação e, se
  // ainda houver cookie de sessão, o middleware CSRF exige o header. Quando
  // token e csrf já expiraram juntos (mesmo TTL), o middleware não exige nada
  // e o refresh passa só com o cookie de refresh.
  refresh: () => request('/auth/refresh', { method: 'POST' }),

  // Pessoas / Cadastro
  listPessoas: (incluirInativos = false) => request(`/cadastro${incluirInativos ? '?incluir_inativos=true' : ''}`),
  getPessoa: (id) => request(`/cadastro/${id}`),
  savePessoa: (pessoa) => request('/cadastro', { method: 'POST', body: pessoa }),
  deactivatePessoa: (id) => request('/cadastro/desativar', { method: 'POST', body: { id } }),
  reactivatePessoa: (id) => request('/cadastro/reativar', { method: 'POST', body: { id } }),
  deactivatePessoasLote: (ids) => request('/cadastro/desativar-lote', { method: 'POST', body: { ids } }),
  reactivatePessoasLote: (ids) => request('/cadastro/reativar-lote', { method: 'POST', body: { ids } }),
  listEquipeCliente: (id) => request(`/cadastro/${id}/equipe`),
  addEquipeCliente: (id, payload) => request(`/cadastro/${id}/equipe`, { method: 'POST', body: payload }),
  removeEquipeCliente: (equipeId) => request(`/equipe/${equipeId}`, { method: 'DELETE' }),
  saveContrato: (id, body) => request(`/cadastro/${id}/contrato`, { method: 'POST', body }),
  listContratos: (id) => request(`/cadastro/${id}/contratos`),
  getContrato: (cid) => request(`/contratos/${cid}`),
  updateContrato: (cid, body) => request(`/contratos/${cid}`, { method: 'PUT', body }),
  cancelarContrato: (cid) => request(`/contratos/${cid}/cancelar`, { method: 'POST' }),
  previewReclassificarContrato: (cid, rateio) => request(`/contratos/${cid}/reclassificar/preview`, { method: 'POST', body: { rateio } }),
  reclassificarContrato: (cid, rateio, sobrescreverCustomizadas) => request(`/contratos/${cid}/reclassificar`, { method: 'POST', body: { rateio, sobrescrever_customizadas: sobrescreverCustomizadas } }),
  vincularContratoAsaas: (pessoaId, cid, body) => request(`/cadastro/${pessoaId}/contratos/${cid}/vincular-asaas`, { method: 'POST', body }),
  listContratoArquivos: (cid) => request(`/contratos/${cid}/arquivos`),
  uploadContratoArquivo: (cid, file) => {
    const fd = new FormData();
    fd.append('arquivo', file);
    return request(`/contratos/${cid}/arquivos`, { method: 'POST', body: fd, headers: { 'Content-Type': undefined } });
  },
  retryContratoArquivo: (cid, aid, file) => {
    const fd = new FormData();
    fd.append('arquivo', file);
    return request(`/contratos/${cid}/arquivos/${aid}/retry`, { method: 'POST', body: fd, headers: { 'Content-Type': undefined } });
  },
  deleteContratoArquivo: (cid, aid) => request(`/contratos/${cid}/arquivos/${aid}`, { method: 'DELETE' }),
  provisionarDrive: (id) => request(`/cadastro/${id}/google-drive/provisionar`, { method: 'POST' }),

  // IA (LLM) — MVP de chat (admin). Ver docs/AI_INTEGRATION_MASTER_PLAN.md.
  iaStatus: () => request('/ia/status'),
  iaChat: (mensagem, historico = []) => request('/ia/chat', { method: 'POST', body: { mensagem, historico } }),

  // Perfil (M3 - Self-service)
  getPdi: () => request('/perfil/pdi'),
  savePdiMeta: (meta) => meta.id ? request(`/perfil/metas/${meta.id}`, { method: 'PUT', body: meta }) : request('/perfil/metas', { method: 'POST', body: meta }),
  deletePdiMeta: (id) => request(`/perfil/metas/${id}`, { method: 'DELETE' }),
  listMetasEmpresa: () => request('/perfil/metas-empresa'),
  listReembolsos: () => request('/perfil/reembolsos'),
  saveReembolso: (r) => request('/perfil/reembolsos', { method: 'POST', body: r }),
  listArquivos: () => request('/perfil/arquivos'),
  downloadArquivo: (id) => request(`/perfil/arquivos/${id}`),
  // Perfil — desenvolvimento (M3 Fase B)
  listCursos: () => request('/perfil/cursos'),
  saveCurso: (c) => (c.id ? request(`/perfil/cursos/${c.id}`, { method: 'PUT', body: c }) : request('/perfil/cursos', { method: 'POST', body: c })),
  deleteCurso: (id) => request(`/perfil/cursos/${id}`, { method: 'DELETE' }),
  listCertificacoes: () => request('/perfil/certificacoes'),
  saveCertificacao: (c) => request('/perfil/certificacoes', { method: 'POST', body: c }),
  deleteCertificacao: (id) => request(`/perfil/certificacoes/${id}`, { method: 'DELETE' }),
  listLicencas: () => request('/perfil/licencas'),
  saveLicenca: (l) => request('/perfil/licencas', { method: 'POST', body: l }),
  deleteLicenca: (id) => request(`/perfil/licencas/${id}`, { method: 'DELETE' }),

  getGamificacao: () => request('/perfil/gamificacao'),
  listGamificacaoRegras: () => request('/gamificacao/regras'),
  saveGamificacaoRegra: (r) => (r.codigo && !r._novo
    ? request(`/gamificacao/regras/${encodeURIComponent(r.codigo)}`, { method: 'PUT', body: r })
    : request('/gamificacao/regras', { method: 'POST', body: r })),
  deleteGamificacaoRegra: (codigo) => request(`/gamificacao/regras/${encodeURIComponent(codigo)}`, { method: 'DELETE' }),
  listBadges: () => request('/gamificacao/badges'),
  saveBadge: (b) => (b.id
    ? request(`/gamificacao/badges/${b.id}`, { method: 'PUT', body: b })
    : request('/gamificacao/badges', { method: 'POST', body: b })),
  deleteBadge: (id) => request(`/gamificacao/badges/${id}`, { method: 'DELETE' }),

  // Funcionários (M2 e M10)
  listSalario: (id) => request(`/funcionarios/${id}/salario`),
  saveSalario: (id, payload) => request(`/funcionarios/${id}/salario`, { method: 'POST', body: payload }),
  listBeneficios: (id) => request(`/funcionarios/${id}/beneficios`),
  saveBeneficio: (id, payload) => request(`/funcionarios/${id}/beneficios`, { method: 'POST', body: payload }),
  deleteBeneficio: (id, bid) => request(`/funcionarios/${id}/beneficios/${bid}`, { method: 'DELETE' }),
  listFuncionarios: () => request('/funcionarios'),
  listEquipe: () => request('/funcionarios/equipe'),
  getInsights: () => request('/funcionarios/insights'),
  listCarteira: (id) => request(`/funcionarios/${id}/carteira`),
  saveCustoFuncionario: (id, body) => request(`/funcionarios/${id}/custo`, { method: 'POST', body }),

  // Financeiro
  // periodo pode ser string 'YYYY-MM' (legado) ou objeto { inicio, fim } (YYYY-MM-DD).
  getDRE: (periodo, regime = 'competencia', cc = '') => {
    const params = new window.URLSearchParams();
    appendPeriodo(params, periodo);
    params.append('regime', regime);
    if (cc) params.append('cc', cc);
    return request(`/financeiro/dre?${params.toString()}`);
  },
  listLancamentos: (filtros) => {
    if (typeof filtros === 'string') filtros = { pessoa_id: filtros };
    else if (!filtros) filtros = {};
    const params = new window.URLSearchParams();
    if (filtros.pessoa_id) params.append('pessoa_id', filtros.pessoa_id);
    if (filtros.q) params.append('q', filtros.q);
    // Busca (q) ignora período no backend — só envia o recorte quando não há busca ativa.
    if (!filtros.q) appendPeriodo(params, filtros.periodo);
    if (filtros.regime) params.append('regime', filtros.regime);
    if (filtros.data_base) params.append('data_base', filtros.data_base);
    if (filtros.incluir_atrasados === false) params.append('incluir_atrasados', 'false');
    if (filtros.status) params.append('status', filtros.status);
    if (filtros.tipo && filtros.tipo !== 'todos') params.append('tipo', filtros.tipo);
    if (filtros.cc) params.append('cc', filtros.cc);
    const qs = params.toString();
    return request(`/financeiro/lancamentos${qs ? `?${qs}` : ''}`);
  },
  getLancamento: (id) => request(`/financeiro/lancamentos/${id}`),
  saveLancamento: (l) =>
    l.id
      ? request(`/financeiro/lancamentos/${l.id}`, { method: 'PUT', body: l })
      : request('/financeiro/lancamentos', { method: 'POST', body: l }),
  baixarLancamento: (id, body) => request(`/financeiro/lancamentos/${id}/baixar`, { method: 'POST', body }),
  deleteLancamento: (id) => request(`/financeiro/lancamentos/${id}`, { method: 'DELETE' }),
  listContas: () => request('/financeiro/contas'),
  saveConta: (c) => c.id ? request(`/financeiro/contas/${c.id}`, { method: 'PUT', body: c }) : request('/financeiro/contas', { method: 'POST', body: c }),
  toggleAtivoConta: (id, ativo) => request(`/financeiro/contas/${id}/ativo`, { method: 'PUT', body: { ativo } }),
  deleteConta: (id) => request(`/financeiro/contas/${id}`, { method: 'DELETE' }),
  listMovimentosConta: (id) => request(`/financeiro/contas/${id}/movimentos`),
  saveMovimento: (m) => request('/financeiro/movimentos', { method: 'POST', body: m }),
  conciliarMovimento: (id, transacaoId) => request(`/financeiro/movimentos/${id}/conciliar`, { method: 'POST', body: { transacao_id: transacaoId } }),
  desconciliarMovimento: (id) => request(`/financeiro/movimentos/${id}/desconciliar`, { method: 'POST' }),
  listCartoes: () => request('/financeiro/cartoes'),
  saveCartao: (c) => c.id ? request(`/financeiro/cartoes/${c.id}`, { method: 'PUT', body: c }) : request('/financeiro/cartoes', { method: 'POST', body: c }),
  toggleAtivoCartao: (id, ativo) => request(`/financeiro/cartoes/${id}/ativo`, { method: 'PUT', body: { ativo } }),
  deleteCartao: (id) => request(`/financeiro/cartoes/${id}`, { method: 'DELETE' }),
  listFaturas: (id) => request(`/financeiro/cartoes/${id}/faturas`),
  listItensFatura: (id, faturaId) => request(`/financeiro/cartoes/${id}/faturas/${faturaId}/itens`),
  updateItemFatura: (id, faturaId, itemId, payload) => request(`/financeiro/cartoes/${id}/faturas/${faturaId}/itens/${itemId}`, { method: 'PUT', body: payload }),
  
  // Contas Proxy
  listContasProxy: (beneficiarioId, ativo) => {
    const params = new window.URLSearchParams();
    if (beneficiarioId) params.append('beneficiario_id', beneficiarioId);
    if (ativo !== undefined) params.append('ativo', ativo);
    const qs = params.toString();
    return request(`/financeiro/contas-proxy${qs ? `?${qs}` : ''}`);
  },
  getContaProxy: (id) => request(`/financeiro/contas-proxy/${id}`),
  createContaProxy: (body) => request('/financeiro/contas-proxy', { method: 'POST', body }),
  updateContaProxy: (id, body) => request(`/financeiro/contas-proxy/${id}`, { method: 'PATCH', body }),
  deleteContaProxy: (id) => request(`/financeiro/contas-proxy/${id}`, { method: 'DELETE' }),
  previewBackfillContaProxy: (id) => request(`/financeiro/contas-proxy/${id}/backfill/preview`, { method: 'POST' }),
  applyBackfillContaProxy: (id) => request(`/financeiro/contas-proxy/${id}/backfill/apply`, { method: 'POST' }),

  // Fila de Triagem (spec 028)
  listTriagem: () => request('/financeiro/triagem'),
  aplicarTriagem: (body) => request('/financeiro/triagem/aplicar', { method: 'POST', body }),
  
  listDespesasRecorrentes: (ativas = false) =>
    request(`/financeiro/despesas-recorrentes${ativas ? '?ativas=true' : ''}`),
  saveDespesaRecorrente: (d) =>
    d.id
      ? request(`/financeiro/despesas-recorrentes/${d.id}`, { method: 'PUT', body: d })
      : request('/financeiro/despesas-recorrentes', { method: 'POST', body: d }),
  deleteDespesaRecorrente: (id) => request(`/financeiro/despesas-recorrentes/${id}`, { method: 'DELETE' }),
  previewGerarRecorrentes: (meses = 3) => request(`/financeiro/despesas-recorrentes/gerar/preview?meses=${meses}`, { method: 'POST' }),
  applyGerarRecorrentes: (meses = 3) => request(`/financeiro/despesas-recorrentes/gerar/apply?meses=${meses}`, { method: 'POST' }),

  lancarDespesaCartao: (id, body) => request(`/financeiro/cartoes/${id}/lancamentos`, { method: 'POST', body }),
  pagarFatura: (id, body) => request(`/financeiro/faturas/${id}/pagar`, { method: 'POST', body }),
  // Assinaturas recorrentes de cartão (spec 017)
  listAssinaturasCartao: (id) => request(`/financeiro/cartoes/${id}/assinaturas`),
  saveAssinaturaCartao: (id, body) => request(`/financeiro/cartoes/${id}/assinaturas`, { method: 'POST', body }),
  deleteAssinaturaCartao: (id, aid) => request(`/financeiro/cartoes/${id}/assinaturas/${aid}`, { method: 'DELETE' }),
  previewAssinaturasCartao: (id, meses = 3) => request(`/financeiro/cartoes/${id}/assinaturas/gerar/preview?meses=${meses}`, { method: 'POST' }),
  applyAssinaturasCartao: (id, meses = 3) => request(`/financeiro/cartoes/${id}/assinaturas/gerar/apply?meses=${meses}`, { method: 'POST' }),
  listCategorias: () => request('/financeiro/categorias'),
  saveCategoria: (c) =>
    c.id
      ? request(`/financeiro/categorias/${c.id}`, { method: 'PUT', body: c })
      : request('/financeiro/categorias', { method: 'POST', body: c }),
  deleteCategoria: (id) => request(`/financeiro/categorias/${id}`, { method: 'DELETE' }),

  listCentrosCusto: (ativos = false) =>
    request(`/financeiro/centros-custo${ativos ? '?ativos=true' : ''}`),
  saveCentroCusto: (c) =>
    c.id
      ? request(`/financeiro/centros-custo/${c.id}`, { method: 'PUT', body: c })
      : request('/financeiro/centros-custo', { method: 'POST', body: c }),
  deleteCentroCusto: (id) => request(`/financeiro/centros-custo/${id}`, { method: 'DELETE' }),

  listComissoes: () => request('/financeiro/comissoes'),
  saveComissao: (c) =>
    c.id
      ? request(`/financeiro/comissoes/${c.id}`, { method: 'PUT', body: c })
      : request('/financeiro/comissoes', { method: 'POST', body: c }),
  deleteComissao: (id) => request(`/financeiro/comissoes/${id}`, { method: 'DELETE' }),

  // Comissões (Lançamentos e Cálculo)
  listComissoesLancamentos: (params) => {
    const qs = new window.URLSearchParams(params || {}).toString();
    return request(`/financeiro/comissoes/lancamentos${qs ? `?${qs}` : ''}`);
  },
  calcularComissoes: (body) => request('/financeiro/comissoes/calcular', { method: 'POST', body }),
  updateComissaoLancamento: (id, body) => request(`/financeiro/comissoes/lancamentos/${id}`, { method: 'PUT', body }),

  // Fechamento DRE
  listFechamentos: () => request('/financeiro/dre/fechamentos'),
  fecharDre: (body) => request('/financeiro/dre/fechar', { method: 'POST', body }),
  reabrirDre: (id) => request(`/financeiro/dre/fechamentos/${id}/reabrir`, { method: 'POST' }),

  // Plano de Contas Hierárquico, Relatórios e Importações
  listPlanoContas: (grupo = '', ativo = '', dre = '') => {
    const params = new window.URLSearchParams();
    if (grupo) params.append('grupo', grupo);
    if (ativo) params.append('ativo', ativo);
    if (dre) params.append('dre', dre);
    const qs = params.toString();
    return request(`/financeiro/plano-contas${qs ? `?${qs}` : ''}`);
  },
  savePlanoContas: (c) =>
    c.id
      ? request(`/financeiro/plano-contas/${c.id}`, { method: 'PUT', body: c })
      : request('/financeiro/plano-contas', { method: 'POST', body: c }),
  saveLancamentoLote: (body) => request('/financeiro/lancamentos/lote', { method: 'POST', body }),
  getDREDinamico: (filtros = {}) => {
    const params = new window.URLSearchParams();
    if (filtros.ano) params.append('ano', filtros.ano);
    if (filtros.periodo) params.append('periodo', filtros.periodo);
    if (filtros.inicio) params.append('inicio', filtros.inicio);
    if (filtros.fim) params.append('fim', filtros.fim);
    if (filtros.regime) params.append('regime', filtros.regime);
    if (filtros.cc) params.append('cc', filtros.cc);
    const qs = params.toString();
    return request(`/financeiro/relatorios/dre${qs ? `?${qs}` : ''}`);
  },
  getBalanco: (filtros = {}) => {
    const params = new window.URLSearchParams();
    if (filtros.data) params.append('data', filtros.data);
    if (filtros.regime) params.append('regime', filtros.regime);
    const qs = params.toString();
    return request(`/financeiro/relatorios/balanco${qs ? `?${qs}` : ''}`);
  },
  getResultadoSocios: (filtros = {}) => {
    const params = new window.URLSearchParams();
    if (filtros.periodo) params.append('periodo', filtros.periodo);
    if (filtros.regime) params.append('regime', filtros.regime);
    const qs = params.toString();
    return request(`/financeiro/relatorios/resultado-socios${qs ? `?${qs}` : ''}`);
  },
  getRelatorioKPIs: (filtros = {}) => {
    const params = new window.URLSearchParams();
    if (filtros.periodo) params.append('periodo', filtros.periodo);
    if (filtros.regime) params.append('regime', filtros.regime);
    const qs = params.toString();
    return request(`/financeiro/relatorios/kpis${qs ? `?${qs}` : ''}`);
  },
  getRelatorioDimensao: (filtros = {}) => {
    const params = new window.URLSearchParams();
    if (filtros.dim) params.append('dim', filtros.dim);
    if (filtros.periodo) params.append('periodo', filtros.periodo);
    if (filtros.regime) params.append('regime', filtros.regime);
    if (filtros.status) params.append('status', filtros.status);
    const qs = params.toString();
    return request(`/financeiro/relatorios/dimensao${qs ? `?${qs}` : ``}`);
  },

  // Aprovações (V2-A01/A02) — fila p/ financeiro/admin; reprovar exige observação.
  listAprovacoes: (tipo = '') => request(`/financeiro/aprovacoes?limit=200${tipo ? `&tipo=${tipo}` : ''}`),
  aprovarReembolso: (id) => request(`/financeiro/aprovacoes/reembolsos/${id}/aprovar`, { method: 'POST' }),
  reprovarReembolso: (id, observacao) =>
    request(`/financeiro/aprovacoes/reembolsos/${id}/reprovar`, { method: 'POST', body: { observacao } }),
  aprovarComissao: (id) => request(`/financeiro/aprovacoes/comissoes/${id}/aprovar`, { method: 'POST' }),
  reprovarComissao: (id, observacao) =>
    request(`/financeiro/aprovacoes/comissoes/${id}/reprovar`, { method: 'POST', body: { observacao } }),

  // Integrações externas (Google Workspace OAuth, Feed Connect/Chatwoot)
  integracoesStatus: () => request('/integracoes/status'),
  googleConnect: () => request('/integracoes/google/connect'),
  googleSyncPhoto: () => request('/integracoes/google/sync-photo', { method: 'POST' }),
  googleDisconnect: () => request('/integracoes/google', { method: 'DELETE' }),

  // Notas do cliente (V2-C04) — CRUD com autor/data; excluir só autor ou admin.
  listNotas: (pessoaId) => request(`/cadastro/${pessoaId}/notas`),
  createNota: (pessoaId, nota) => request(`/cadastro/${pessoaId}/notas`, { method: 'POST', body: { nota } }),
  updateNota: (id, nota) => request(`/cadastro/notas/${id}`, { method: 'PUT', body: { nota } }),
  deleteNota: (id) => request(`/cadastro/notas/${id}`, { method: 'DELETE' }),

  // Dashboard agregado por perfil
  dashboard: (periodo) => {
    const params = new window.URLSearchParams();
    appendPeriodo(params, periodo);
    const qs = params.toString();
    return request(`/dashboard${qs ? `?${qs}` : ''}`);
  },

  // Config
  listUsuarios: () => request('/config/usuarios'),
  updateUsuario: (id, body) => request(`/config/usuarios/${id}`, { method: 'PUT', body }),
  uploadFoto: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return request(`/config/usuarios/${id}/foto`, {
      method: 'POST',
      body: formData,
      headers: {
        // Allow browser to set content-type for multipart/form-data
        'Content-Type': undefined,
      },
    });
  },
  uploadFotoPerfil: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return request(`/perfil/foto`, {
      method: 'POST',
      body: formData,
      headers: {
        'Content-Type': undefined,
      },
    });
  },
  uploadComprovante: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return request('/financeiro/comprovantes/upload', {
      method: 'POST',
      body: formData,
      headers: {
        'Content-Type': undefined,
      },
    });
  },
  // STOR-01: comprovantes vivem em bucket privado — a leitura exige presigned URL (1h).
  getComprovanteURL: (key) => request(`/financeiro/comprovantes/url?key=${encodeURIComponent(key)}`),
  // NOTIF-01 — Central de Notificações
  listNotificacoes: (limit = 20) => request(`/notificacoes/?limit=${limit}`),
  countNotificacoesNaoLidas: () => request('/notificacoes/nao-lidas'),
  marcarNotificacaoLida: (id) => request(`/notificacoes/${id}/lida`, { method: 'POST' }),
  marcarTodasNotificacoesLidas: () => request('/notificacoes/lidas', { method: 'POST' }),
  // UX-01 — Presença: heartbeat marca o usuário online e devolve quem mais está
  presencaHeartbeat: () => request('/presenca/heartbeat', { method: 'POST' }),
  listSetores: () => request('/config/setores'),
  getCatalogos: () => request('/config/catalogos'),
  getCatalogo: (dominio) => request(`/config/catalogos/${encodeURIComponent(dominio)}`),
  createCatalogoOpcao: (body) => request('/config/catalogos', { method: 'POST', body }),
  updateCatalogoOpcao: (id, body) => request(`/config/catalogos/${id}`, { method: 'PUT', body }),
  deleteCatalogoOpcao: (id) => request(`/config/catalogos/${id}`, { method: 'DELETE' }),
  listConfigSistema: () => request('/config/sistema'),
  updateConfigSistema: (chave, valor) => request(`/config/sistema/${encodeURIComponent(chave)}`, { method: 'PUT', body: { valor } }),
  getIntegracaoConfig: (provedor) => request(`/integracoes/config?provedor=${provedor}`),
  saveIntegracaoConfig: (data) => request(`/integracoes/config`, { method: 'POST', body: data }),
  getMonitoramentoLogs: (provedor) => request(`/integracoes/monitoramento?provedor=${provedor}`),
  forceWorkerSync: (provedor) => request(`/integracoes/monitoramento/force`, { method: 'POST', body: { provedor } }),
  patchWorkerSettings: (data) => request('/integracoes/monitoramento/settings', { method: 'PATCH', body: data }),

  // Asaas espelho (leitura) — Fase 4a
  asaasSaldo: () => request('/integracoes/asaas/saldo'),
  asaasExtrato: (limit = 100) => request(`/integracoes/asaas/extrato?limit=${limit}`),
  asaasTransferencias: (limit = 100) => request(`/integracoes/asaas/transferencias?limit=${limit}`),
  asaasAntecipacoes: (limit = 100) => request(`/integracoes/asaas/antecipacoes?limit=${limit}`),
  asaasNotasFiscais: (limit = 100) => request(`/integracoes/asaas/notas-fiscais?limit=${limit}`),

  // SYS-MON — Observabilidade (admin) — contrato Rodada 3; backend: Claude CLI
  getObservabilidadeResumo: () => request('/sistemas/observabilidade/resumo'),
  getObservabilidadePresenca: () => request('/sistemas/observabilidade/presenca'),
  getObservabilidadeLogins: (filters = {}) => {
    const params = new window.URLSearchParams();
    if (filters.inicio) params.set('inicio', filters.inicio);
    if (filters.fim) params.set('fim', filters.fim);
    if (filters.sucesso != null && filters.sucesso !== '') params.set('sucesso', filters.sucesso);
    if (filters.usuario_login) params.set('usuario_login', filters.usuario_login);
    if (filters.limit) params.set('limit', String(filters.limit));
    if (filters.offset) params.set('offset', String(filters.offset));
    const qs = params.toString();
    return request(`/sistemas/observabilidade/logins${qs ? `?${qs}` : ''}`);
  },
  getObservabilidadeEventos: (filters = {}) => {
    const params = new window.URLSearchParams();
    if (filters.inicio) params.set('inicio', filters.inicio);
    if (filters.fim) params.set('fim', filters.fim);
    if (filters.acao) params.set('acao', filters.acao);
    if (filters.entidade) params.set('entidade', filters.entidade);
    if (filters.usuario_login) params.set('usuario', filters.usuario_login);
    if (filters.limit) params.set('limit', String(filters.limit));
    if (filters.offset) params.set('offset', String(filters.offset));
    const qs = params.toString();
    return request(`/sistemas/observabilidade/eventos${qs ? `?${qs}` : ''}`);
  },
  getObservabilidadeIntegracoes: (filters = {}) => {
    const params = new window.URLSearchParams();
    if (filters.provedor) params.set('provedor', filters.provedor);
    if (filters.status) params.set('status', filters.status);
    if (filters.inicio) params.set('inicio', filters.inicio);
    if (filters.fim) params.set('fim', filters.fim);
    if (filters.limit) params.set('limit', String(filters.limit));
    if (filters.offset) params.set('offset', String(filters.offset));
    const qs = params.toString();
    return request(`/sistemas/observabilidade/integracoes${qs ? `?${qs}` : ''}`);
  },
  getObservabilidadeAlertas: (filters = {}) => {
    const params = new window.URLSearchParams();
    if (filters.limit) params.set('limit', String(filters.limit));
    const qs = params.toString();
    return request(`/sistemas/observabilidade/alertas${qs ? `?${qs}` : ''}`);
  },
  getIACustos: (periodo) => {
    const qs = periodo ? `?periodo=${periodo}` : '';
    return request(`/sistemas/observabilidade/ia-custos${qs}`);
  },

  saveSetor: (s) => (s.id ? request(`/config/setores/${s.id}`, { method: 'PUT', body: s }) : request('/config/setores', { method: 'POST', body: s })),
  deleteSetor: (id) => request(`/config/setores/${id}`, { method: 'DELETE' }),
  listSociedade: () => request('/config/sociedade'),
  saveSocio: (s) =>
    s.id
      ? request(`/config/sociedade/socios/${s.id}`, { method: 'PUT', body: s })
      : request('/config/sociedade/socios', { method: 'POST', body: s }),
  deleteSocio: (id) => request(`/config/sociedade/socios/${id}`, { method: 'DELETE' }),

  // TES-01: Tesouraria
  getTesourariaAnalise: () => request('/financeiro/tesouraria/analise'),
  tesourariaTransferir: (body) => request('/financeiro/tesouraria/transferir', { method: 'POST', body }),
  saveTesourariaConfig: (body) => request('/financeiro/tesouraria/config', { method: 'PUT', body }),

  // TES-01: Fechamento Anual / PLR
  listExerciciosAnuais: () => request('/financeiro/exercicio'),
  fecharExercicio: (body) => request('/financeiro/exercicio/fechar', { method: 'POST', body }),

  // Épico 2: Transferência entre contas + Fluxo de Caixa projetado
  criarTransferencia: (body) => request('/financeiro/transferencias', { method: 'POST', body }),
  getFluxoCaixa: (dias = 30) => request(`/financeiro/fluxo-caixa?dias=${dias}`),
};
